import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import classNames from 'classnames';
import styles from './FJTextSelectorContainer.module.less';
import { useAppDispatch, useAppSelector } from '@src/redux/hooks.ts';
import FJTrimAction from '@src/redux/action/FJTrimAction.ts';
import FJTrimVideoManager from '@src/manager/FJTrimVideoManager.ts';
import FJUtil from '@src/util/FJUtil';
import FJLocalStore from '@src/store/FJLocalStore';
import FJUserBehaviorStore from '@src/store/FJUserBehaviorStore';

// 字幕对象接口
interface Subtitle {
    text: string;
    start: number;
    end: number;
    isTranslated: boolean;
    transcriptionText: string;
    transcriptionWords: any[];
}

// 包含时间戳的单词接口
interface Word {
    text: string;
    startTime: number;
    endTime: number;
}

interface WordLocation {
    sIdx: number;
    wIdx: number;
}

/** 气泡与标记间距，与 `.crop_tip` 的 16px 一致 */
const CROP_TIP_VERTICAL_GAP = 16;
/** 未渲染实测前的预估高度（padding + 一行文案） */
const CROP_TIP_ESTIMATED_HEIGHT = 36;

const FJTextSelectorContainer: React.FC = () => {
    const dispatch = useAppDispatch();
    const { subtitleList, startTime, endTime, isShow, currentTime } = useAppSelector(state => ({
        subtitleList: state.modal.cut.subtitleList,
        startTime: state.modal.cut.startTime,
        endTime: state.modal.cut.endTime,
        isShow: state.modal.cut.isShow,
        currentTime: state.modal.cut.currentTime,
    }));

    const [isSelecting, setIsSelecting] = useState(false);
    const [cropTipEnabled, setCropTipEnabled] = useState(false);
    const [cropTipBelow, setCropTipBelow] = useState(false);
    const selectionAnchorRef = useRef<{ type: 'start' | 'end'; time: number } | null>(null);
    const [draggedBoundary, setDraggedBoundary] = useState<'start' | 'end' | null>(null);
    const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
    const containerRef = useRef<HTMLDivElement>(null); // Ref for the main scrollable container
    const cropTipRef = useRef<HTMLDivElement>(null);
    const autoPositioning = useRef(true);
    /** 本次按下是否已因拖动关闭新手引导（仅点击不关闭） */
    const cropTipDismissedByDragRef = useRef(false);

    const duration = useMemo(() => {
        if (!subtitleList || subtitleList.length === 0) {
            return 0;
        } else if (subtitleList.length === 1) {
            return subtitleList[0].end;
        } else {
            const startData: Subtitle = subtitleList[0];
            const endData: Subtitle = subtitleList[subtitleList.length - 1];
            return endData.end - startData.start;
        }
    }, [subtitleList]);

    // 预计算每个单词和空格的时间戳
    const timedSubtitleList = useMemo(() => {
        // 检查浏览器是否支持 Intl.Segmenter
        if (!('Segmenter' in Intl)) {
            console.warn('Intl.Segmenter is not supported in this browser. Falling back to space-based splitting.');
            // 在不支持的环境中，可以保留旧的逻辑或提示用户
        }

        // 创建一个分词器实例，它可以自动适应不同语言
        const segmenter = new Intl.Segmenter(undefined, { granularity: 'word' });

        return subtitleList.map((subtitle: Subtitle) => {
            const segments = Array.from(segmenter.segment(subtitle.text));

            // 计算有效单词（非空格）的数量，用于分配时间
            const wordCount = segments.filter(seg => seg.segment.trim().length > 0).length;
            const durationPerWord = wordCount > 0 ? (subtitle.end - subtitle.start) / wordCount : 0;

            let currentTime = subtitle.start;
            const words: Word[] = segments.map(seg => {
                const wordText = seg.segment;
                // 只为有实际内容的单词分配时间，空格等不占用时间
                if (wordText.trim().length > 0) {
                    const wordStartTime = currentTime;
                    const wordEndTime = currentTime + durationPerWord;
                    currentTime = wordEndTime;
                    return { text: wordText, startTime: wordStartTime, endTime: wordEndTime };
                } else {
                    // 对于空格或标点符号，它们不消耗时间，但我们保留它们以维持布局
                    return { text: wordText, startTime: currentTime, endTime: currentTime };
                }
            });
            return { ...subtitle, words };
        });
    }, [subtitleList]);

    const changeStartTime = useCallback((time: number) => dispatch(FJTrimAction.setStartTimeAction(time)), [dispatch]);
    const changeEndTime = useCallback((time: number) => dispatch(FJTrimAction.setEndTimeAction(time)), [dispatch]);

    const dismissCropTip = useCallback(() => {
        setCropTipEnabled(false);
        void FJUserBehaviorStore.setCropTipSeen();
    }, []);

    const dismissCropTipOnDrag = useCallback(() => {
        if (cropTipDismissedByDragRef.current || !cropTipEnabled) {
            return;
        }
        cropTipDismissedByDragRef.current = true;
        dismissCropTip();
    }, [cropTipEnabled, dismissCropTip]);

    const showCropTip = isShow && cropTipEnabled;

    const handleStartMarkerMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        cropTipDismissedByDragRef.current = false;
        setIsSelecting(true);
        setDraggedBoundary('start');
        selectionAnchorRef.current = { type: 'end', time: Math.max(startTime, endTime) };
    };

    const handleEndMarkerMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        cropTipDismissedByDragRef.current = false;
        setIsSelecting(true);
        setDraggedBoundary('end');
        selectionAnchorRef.current = { type: 'start', time: Math.min(startTime, endTime) };
    };

    const getFilteredSubtitleList = useCallback(() => {
        const currentStartTime = Math.min(startTime, endTime);
        const currentEndTime = Math.max(startTime, endTime);
        const filteredList: Subtitle[] = [];

        timedSubtitleList.forEach(subtitle => {
            const selectedWords = subtitle.words.filter(word => {
                const isSpace = word.text.trim().length === 0;
                if (isSpace) {
                    return word.startTime > currentStartTime && word.startTime < currentEndTime;
                } else {
                    return word.startTime < currentEndTime && word.endTime > currentStartTime;
                }
            });

            if (selectedWords.length > 0) {
                const newText = selectedWords.map(w => w.text).join('');
                const newStart = Math.max(selectedWords[0].startTime, currentStartTime);
                const newEnd = Math.min(selectedWords[selectedWords.length - 1].endTime, currentEndTime);
                filteredList.push({
                    text: newText,
                    start: newStart,
                    end: newEnd,
                    isTranslated: subtitle.isTranslated,
                    transcriptionText: subtitle.transcriptionText,
                    transcriptionWords: subtitle.transcriptionWords,
                });
            }
        });
        return filteredList;
    }, [startTime, endTime, timedSubtitleList]);

    const seekTime = (time: number) => {
        if (time < startTime || time > endTime) {
            return;
        }
        dispatch(FJTrimAction.seekAction(time));
    };

    const handleWordUpdate = useCallback(
        (word: Word) => {
            if (!isSelecting) return;

            const anchor = selectionAnchorRef.current;
            if (!anchor) return;

            if (draggedBoundary === 'start') {
                const newStartTime = word.startTime;
                const fixedEndTime = anchor.time;

                if (newStartTime > fixedEndTime) {
                    changeStartTime(fixedEndTime);
                    changeEndTime(newStartTime);
                    selectionAnchorRef.current = { type: 'start', time: fixedEndTime };
                    setDraggedBoundary('end');
                } else {
                    changeStartTime(newStartTime);
                    changeEndTime(fixedEndTime);
                }
            } else if (draggedBoundary === 'end') {
                const newEndTime = word.endTime;
                const fixedStartTime = anchor.time;

                if (newEndTime < fixedStartTime) {
                    changeStartTime(newEndTime);
                    changeEndTime(fixedStartTime);
                    selectionAnchorRef.current = { type: 'end', time: fixedStartTime };
                    setDraggedBoundary('start');
                } else {
                    changeStartTime(fixedStartTime);
                    changeEndTime(newEndTime);
                }
            }
        },
        [isSelecting, draggedBoundary, selectionAnchorRef, changeStartTime, changeEndTime],
    );

    useEffect(() => {
        if (!isShow) {
            return;
        }
        let active = true;
        let timer: number | undefined;
        void FJUserBehaviorStore.hasSeenCropTip().then(seen => {
            if (active && !seen) {
                timer = window.setTimeout(() => {
                    if (active) {
                        setCropTipEnabled(true);
                    }
                }, 450);
            }
        });
        return () => {
            active = false;
            if (timer) {
                window.clearTimeout(timer);
            }
            window.setTimeout(() => {
                setCropTipEnabled(false);
            }, 0);
        };
    }, [isShow]);

    useEffect(() => {
        if (isShow && !(FJTrimVideoManager.trimSubtitleList && FJTrimVideoManager.trimSubtitleList.length > 0)) {
            const filteredList = getFilteredSubtitleList();
            dispatch(FJTrimAction.setFilteredSubtitleListAction(filteredList));
        }
    }, [isShow, getFilteredSubtitleList, dispatch]);

    useEffect(() => {
        // This function finds the word element closest to the mouse cursor's position.
        // It iterates through all word spans in all subtitles and calculates the
        // Euclidean distance from the mouse coordinates to the word span's bounding box.
        const findWordFromMouseEvent = (e: MouseEvent): Word | null => {
            const { clientX, clientY } = e;
            let closestWord: Word | null = null;
            let minDistanceSquared = Infinity; // Use squared distance to avoid sqrt in loop

            timedSubtitleList.forEach((subtitle, sIdx) => {
                const rowEl = rowRefs.current[sIdx];
                if (!rowEl) return;

                const wordSpans = Array.from(rowEl.querySelectorAll(`.${styles.word}[data-widx]`));
                wordSpans.forEach(wordSpanEl => {
                    const rect = wordSpanEl.getBoundingClientRect();
                    const wIdx = parseInt((wordSpanEl as HTMLElement).dataset.widx!, 10);
                    const word = subtitle.words[wIdx];

                    // Skip words that are just spaces, as we want to snap to actual text
                    if (!word || word.text.trim().length === 0) return;

                    // Calculate the closest point on the rectangle to the mouse cursor
                    const closestX = Math.max(rect.left, Math.min(clientX, rect.right));
                    const closestY = Math.max(rect.top, Math.min(clientY, rect.bottom));

                    // Calculate Euclidean distance squared
                    const dx = clientX - closestX;
                    const dy = clientY - closestY;
                    const distanceSquared = dx * dx + dy * dy;

                    if (distanceSquared < minDistanceSquared) {
                        minDistanceSquared = distanceSquared;
                        closestWord = word;
                    }
                });
            });
            return closestWord;
        };

        const handleGlobalMouseMove = (e: MouseEvent) => {
            e.preventDefault();
            dismissCropTipOnDrag();
            const word = findWordFromMouseEvent(e);
            if (word) {
                handleWordUpdate(word);
            }
        };

        const handleGlobalMouseUp = () => {
            cropTipDismissedByDragRef.current = false;
            setIsSelecting(false);
            setDraggedBoundary(null);
            selectionAnchorRef.current = null;
            dispatch(FJTrimAction.updateVideoCurrentTimeWhenZoneChangedAction());
            const filteredList = getFilteredSubtitleList();
            dispatch(FJTrimAction.setFilteredSubtitleListAction(filteredList));
        };

        if (isSelecting) {
            window.addEventListener('mousemove', handleGlobalMouseMove);
            window.addEventListener('mouseup', handleGlobalMouseUp, { once: true });
        }

        return () => {
            window.removeEventListener('mousemove', handleGlobalMouseMove);
            window.removeEventListener('mouseup', handleGlobalMouseUp);
        };
    }, [isSelecting, dispatch, timedSubtitleList, handleWordUpdate, getFilteredSubtitleList, dismissCropTipOnDrag]);

    const isWordSelected = (word: Word): boolean => {
        const isSpace = word.text.trim().length === 0;
        const currentStartTime = Math.min(startTime, endTime);
        const currentEndTime = Math.max(startTime, endTime);

        if (isSpace) {
            return word.startTime > currentStartTime && word.startTime < currentEndTime;
        } else {
            return word.startTime < currentEndTime && word.endTime > currentStartTime;
        }
    };

    const isWordPlaying = (word: Word): boolean => {
        if (word.text.trim().length === 0) return false;
        return currentTime >= word.startTime && currentTime < word.endTime;
    };

    const findStartWordLocation = useCallback(
        (time: number): WordLocation | null => {
            for (let sIdx = 0; sIdx < timedSubtitleList.length; sIdx++) {
                const sub = timedSubtitleList[sIdx];
                for (let wIdx = 0; wIdx < sub.words.length; wIdx++) {
                    const word = sub.words[wIdx];
                    if (word.text.trim().length === 0) continue;
                    if (time >= word.startTime && time < word.endTime) {
                        return { sIdx, wIdx };
                    }
                }
            }

            let bestCandidate: WordLocation | null = null;
            let minDistance = Infinity;
            for (let sIdx = 0; sIdx < timedSubtitleList.length; sIdx++) {
                const sub = timedSubtitleList[sIdx];
                for (let wIdx = 0; wIdx < sub.words.length; wIdx++) {
                    const word = sub.words[wIdx];
                    if (word.text.trim().length === 0) continue;
                    const distance = word.startTime - time;
                    if (distance >= 0 && distance < minDistance) {
                        minDistance = distance;
                        bestCandidate = { sIdx, wIdx };
                    }
                }
            }
            return bestCandidate;
        },
        [timedSubtitleList],
    );

    const findEndWordLocation = useCallback(
        (time: number): WordLocation | null => {
            for (let sIdx = 0; sIdx < timedSubtitleList.length; sIdx++) {
                const sub = timedSubtitleList[sIdx];
                for (let wIdx = 0; wIdx < sub.words.length; wIdx++) {
                    const word = sub.words[wIdx];
                    if (word.text.trim().length === 0) continue;
                    if (time > word.startTime && time <= word.endTime) {
                        return { sIdx, wIdx };
                    }
                }
            }

            let bestCandidate: WordLocation | null = null;
            let minDistance = Infinity;
            for (let sIdx = 0; sIdx < timedSubtitleList.length; sIdx++) {
                const sub = timedSubtitleList[sIdx];
                for (let wIdx = 0; wIdx < sub.words.length; wIdx++) {
                    const word = sub.words[wIdx];
                    if (word.text.trim().length === 0) continue;
                    const distance = time - word.endTime;
                    if (distance >= 0 && distance < minDistance) {
                        minDistance = distance;
                        bestCandidate = { sIdx, wIdx };
                    }
                }
            }
            return bestCandidate;
        },
        [timedSubtitleList],
    );

    const startMarkerLocation = useMemo<WordLocation | null>(() => {
        return findStartWordLocation(Math.min(startTime, endTime));
    }, [startTime, endTime, findStartWordLocation]);

    const endMarkerLocation = useMemo<WordLocation | null>(() => {
        return findEndWordLocation(Math.max(startTime, endTime));
    }, [startTime, endTime, findEndWordLocation]);

    const updateCropTipPlacement = useCallback(() => {
        if (!showCropTip || !startMarkerLocation || !containerRef.current) {
            return;
        }
        const { sIdx, wIdx } = startMarkerLocation;
        const rowEl = rowRefs.current[sIdx];
        if (!rowEl) {
            return;
        }
        const wordSpan = rowEl.querySelector(`span[data-widx="${wIdx}"]`) as HTMLElement | null;
        if (!wordSpan) {
            return;
        }
        const containerRect = containerRef.current.getBoundingClientRect();
        const markerRect = wordSpan.getBoundingClientRect();
        const spaceAbove = markerRect.top - containerRect.top;
        const tipHeight = cropTipRef.current?.offsetHeight ?? CROP_TIP_ESTIMATED_HEIGHT;
        const needed = tipHeight + CROP_TIP_VERTICAL_GAP;
        setCropTipBelow(spaceAbove < needed);
    }, [showCropTip, startMarkerLocation]);

    useLayoutEffect(() => {
        if (!showCropTip) {
            setCropTipBelow(false);
            return;
        }
        const measure = () => updateCropTipPlacement();
        measure();
        const raf = requestAnimationFrame(measure);
        return () => cancelAnimationFrame(raf);
    }, [showCropTip, startMarkerLocation, updateCropTipPlacement]);

    useEffect(() => {
        if (!showCropTip || !containerRef.current) {
            return;
        }
        const el = containerRef.current;
        const onScroll = () => updateCropTipPlacement();
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => el.removeEventListener('scroll', onScroll);
    }, [showCropTip, updateCropTipPlacement]);

    useEffect(() => {
        if (!isShow || !showCropTip) {
            return;
        }
        const t = window.setTimeout(updateCropTipPlacement, 400);
        return () => window.clearTimeout(t);
    }, [isShow, showCropTip, startMarkerLocation, updateCropTipPlacement]);

    useEffect(() => {
        autoPositioning.current = isShow;
    }, [isShow]);

    useEffect(() => {
        if (isShow && startMarkerLocation && containerRef.current && autoPositioning.current) {
            autoPositioning.current = false;
            const { sIdx, wIdx } = startMarkerLocation;
            const rowEl = rowRefs.current[sIdx];
            if (rowEl) {
                const wordSpan = rowEl.querySelector(`span[data-widx="${wIdx}"]`) as HTMLElement;
                if (wordSpan) {
                    setTimeout(() => {
                        wordSpan.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }, 200);
                }
            }
        }
    }, [isShow, startMarkerLocation]);

    return (
        <div className={styles.box}>
            <div ref={containerRef} className={`${styles.textSelectorContainer} ${isSelecting && styles.cursor}`}>
                {timedSubtitleList.map((subtitle, subtitleIndex) => (
                    <div
                        key={subtitleIndex}
                        className={styles.subtitleRow}
                        ref={el => (rowRefs.current[subtitleIndex] = el)}
                    >
                        <div className={styles.timestamp}>
                            {FJUtil.formatTime(subtitle.start, false, 2, 0, true, duration > 60 * 60)}
                        </div>
                        <div className={styles.textContent}>
                            {subtitle.words.map((word, wordIndex) => {
                                const isStartMarker =
                                    startMarkerLocation &&
                                    startMarkerLocation.sIdx === subtitleIndex &&
                                    startMarkerLocation.wIdx === wordIndex;
                                const isEndMarker =
                                    endMarkerLocation &&
                                    endMarkerLocation.sIdx === subtitleIndex &&
                                    endMarkerLocation.wIdx === wordIndex;
                                const isSelected = isWordSelected(word);

                                return (
                                    <span
                                        key={wordIndex}
                                        className={classNames(styles.word, {
                                            [styles.selected]: isSelected,
                                            [styles.playing]: isWordPlaying(word),
                                            [styles.disabled]: !isSelected,
                                        })}
                                        data-widx={wordIndex}
                                        onClick={isSelected ? () => seekTime(word.startTime) : undefined}
                                    >
                                        {isStartMarker && (
                                            <>
                                                <div
                                                    className={styles.startMarker}
                                                    onMouseDown={handleStartMarkerMouseDown}
                                                />
                                                {showCropTip && (
                                                    <div
                                                        ref={cropTipRef}
                                                        className={classNames(styles.crop_tip, {
                                                            [styles.crop_tip_below]: cropTipBelow,
                                                        })}
                                                        role='tooltip'
                                                    >
                                                        {FJLocalStore._('crop-drag-select-tip')}
                                                        <span className={styles.crop_tip_arrow} aria-hidden />
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        {word.text}
                                        {isEndMarker && (
                                            <div className={styles.endMarker} onMouseDown={handleEndMarkerMouseDown} />
                                        )}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default FJTextSelectorContainer;
